// reportsService.js
// Service layer for the Reports feature. Wraps useApi for all report-template
// related endpoints (list/create/update/delete + supporting catalog APIs)
// so components don't need to know endpoint names or header conventions.

import useApi from '../hooks/useApi';

const REPORTS_HEADERS = ['accountId', 'token', 'apiKey'];

/**
 * Fetch a single page of reports for the current user.
 *
 * Backend signature (PUT /reportlist/{userId}/{page}?searchText=...):
 *   body: { sortBy, sortDir, dataFilter: [{ field, operator, value }] }
 *   response: { info: [...], details: { muiPaginationLimit, count, allowUserToCreateNewReport } }
 *
 * @param {Object}   options
 * @param {number}   [options.page=0]
 * @param {string}   [options.searchText='']
 * @param {string}   [options.sortBy]      one of "name" | "description" | "createdAt"
 * @param {string}   [options.sortDir]     "asc" | "desc"
 * @param {Array}    [options.dataFilter]  [{ field: "name"|"description", operator, value }]
 */
export const fetchReportList = async ({ page = 0, searchText = '', sortBy, sortDir, dataFilter } = {}) => {
  const data = {};
  if (sortBy) data.sortBy = sortBy;
  if (sortDir) data.sortDir = sortDir;
  if (Array.isArray(dataFilter) && dataFilter.length > 0) data.dataFilter = dataFilter;
  return useApi(
    'REPORT_LIST',
    { page, searchText: searchText ? `?searchText=${encodeURIComponent(searchText)}` : '' },
    { data },
    REPORTS_HEADERS
  );
};

/** Catalog of widgets that may be added to a report. */
export const fetchWidgetList = async () => {
  return useApi('GET_WIDGET_LIST', {}, {}, REPORTS_HEADERS);
};

/** Org -> Network tree used by the filter step. */
export const fetchOrgNetDetails = async () => {
  return useApi('GET_ORG_NET_DETAILS_LIST', {}, {}, REPORTS_HEADERS);
};

/** Saved filters (tags) used for the "Use Existing" filter option. */
export const fetchFilterList = async () => {
  return useApi('GET_FILTER_LIST', {}, {}, REPORTS_HEADERS);
};

/** Full template details for a given report (used to pre-fill edit mode). */
export const fetchReportDetails = async (reportId) => {
  return useApi('GET_REPORT_DETAILS', { reportId }, {}, REPORTS_HEADERS);
};

/** Server-side validation of a report name (uniqueness). */
export const validateReportName = async (name) => {
  return useApi('VALIDATE_REPORT_NAME', {}, { data: { reportInfo: { name } } }, REPORTS_HEADERS);
};

/** Persist a brand-new report template. */
export const saveReport = async (payload) => {
  return useApi('SAVE_REPORT', {}, { data: payload }, REPORTS_HEADERS);
};

/** Update an existing report template. */
export const updateReport = async (reportId, payload) => {
  return useApi('UPDATE_REPORT', { reportId }, { data: payload }, REPORTS_HEADERS);
};

/**
 * Bulk-delete one or more report templates.
 *
 * Mirrors the device-list bulk delete contract:
 *   DELETE organization/v2/reportType/{_Id}
 *   body: ["<reportTemplateId>", "<reportTemplateId>", ...]
 *   response: { response: { status }, details: { successDeleted: [...], notFound: [...] } }
 *
 * Accepts either a single id (string|number) or an array of ids.
 */
export const deleteReport = async (reportIds) => {
  const ids = Array.isArray(reportIds) ? reportIds : [reportIds];
  return useApi('DELETE_REPORT', {}, { data: ids }, REPORTS_HEADERS);
};

/**
 * Duplicate an existing report template under a new name.
 *
 * Mirrors the classic UI's `copyReport` reducer (`reducers/reports.js`):
 *   POST organization/v1/copytemplate/{_Id}/{reportId}
 *   body: { name: "<new report name>" }
 *
 * The caller is expected to validate uniqueness via {@link validateReportName}
 * BEFORE calling this — the classic Copy Report flow runs the check first
 * and only invokes copy on success.
 */
export const copyReport = async (reportId, name) => {
  return useApi('COPY_REPORT', { reportId }, { data: { name } }, REPORTS_HEADERS);
};

/**
 * Update the email recipients for a report template (the per-row
 * "Email Report" action on the list page).
 *
 * Mirrors the classic UI's `updateEmails` reducer (`reducers/reports.js`):
 *   PUT organization/v1/email/{_Id}/{reportId}
 *   body: { emailRecipients: ["a@b.com", "c@d.com", ...] }
 *
 * The classic flow splits the textarea on "," with no trim, but we trim each
 * entry here so the payload stays clean even if the user typed extra spaces.
 *
 * @param {string|number} reportId
 * @param {string[]} emailRecipients   Already-validated list of addresses.
 */
export const updateReportEmails = async (reportId, emailRecipients) => {
  return useApi('EMAIL_REPORT', { reportId }, { data: { emailRecipients } }, REPORTS_HEADERS);
};

/**
 * Fetch the list of generated PDFs for a report template.
 *
 * Mirrors the classic UI's `viewReport` reducer (`reducers/reports.js`):
 *   GET organization/v1/reporturl/{_Id}/{reportId}
 *   response: { details: { pdfList: [{ name, createdAt, url }] } }
 *
 * Used by the per-row "View/Download" action to populate the dialog table.
 */
export const fetchReportPdfList = async (reportId) => {
  return useApi('VIEW_REPORT', { reportId }, {}, REPORTS_HEADERS);
};

/**
 * Download a browsing-history report as a zip blob.
 *
 * Mirrors classic `list.js > downloadBrowsingHistory` +
 * `Api.js > downLoadBrowserHistory`:
 *   GET organization/v1/downloadreport/{reportTemplateId}/{_Id}
 *   responseType: blob — returns zip bytes, or application/json on error.
 */
export const downloadBrowsingHistoryReport = async (reportId) => {
  return useApi('DOWNLOAD_BROWSING_HISTORY_REPORT', { reportId }, { responseType: 'blob' }, REPORTS_HEADERS);
};

/**
 * Fetch the device list for one network — used by the Filter step's
 * "Change" picker to let the user choose specific devices for a location.
 *
 * Mirrors the classic UI's `getNetDeviceDetails` reducer (built on the
 * `getNetDeviceDetailsList` URL mapping in `components/common/Api.js`):
 *   GET  organization/v1/devicefilters/{_Id}
 *   header: { networkId: <id> }
 *   response: { details: [{ deviceType, serialNo, deviceName, ... }] }
 */
export const fetchNetDeviceDetailsList = async (networkId) => {
  return useApi('GET_NET_DEVICE_DETAILS_LIST', {}, { headers: { networkId } }, REPORTS_HEADERS);
};

/**
 * Display labels for the device-type buckets returned by the network
 * device API. Used by the "Select Devices" dialog accordion headers.
 *
 * Codes are kept byte-identical to the classic UI's switch statement in
 * `reportConfig.js > getDeviceList` (lines ~478-514).
 *
 * Label strings mirror the classic localization keys used inside the
 * `ChangeOrganization` modal (`reportConfig.js` lines 2119/2151/2183/
 * 2215/2247/2279):
 *   localization.orbi        = "Orbi"
 *   localization.switch      = "Switch"
 *   localization.accessPoint = "Access Point"
 *   localization.br500       = "BR500"        (model-series label, not "Bridges")
 *   localization.pr          = "Pro Router"   (product-line label, not "Routers")
 *   localization.mhs         = "Mobile"       (product-line label, not "Cell")
 */
export const DEVICE_TYPE_LABELS = {
  ORBI: 'Orbi',
  SW: 'Switch',
  AP: 'Access Point',
  BR: 'BR500',
  PR: 'Pro Router',
  MHS: 'Mobile',
  AV: 'AV'
};

/** Stable display order for the device-type accordions. */
export const DEVICE_TYPE_ORDER = ['ORBI', 'SW', 'AP', 'BR', 'PR', 'AV', 'MHS'];

// ------------------------------------------------------------------ //
// Helpers used by both the list page and the wizard
// ------------------------------------------------------------------ //

/** Map schedule code -> label shown in the list table. */
export const SCHEDULE_LABELS = {
  1: 'Run Once',
  2: 'Weekly',
  3: 'Monthly'
};

/**
 * Today's date as a YYYY-MM-DD string in the user's LOCAL timezone.
 * Do NOT use `new Date().toISOString().slice(0,10)` for "today" — that returns
 * the UTC date, which is already tomorrow for users west of UTC between UTC
 * midnight and local midnight, letting a "no future dates" check accept a date
 * that is still in the user's future.
 */
export const todayLocalISODate = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

/**
 * Time-period dropdown options (Step 1 of the wizard).
 *
 * The classic UI (`reportConfig.js`) shows DIFFERENT option sets depending on
 * the report template type, so we expose two arrays:
 *
 *   - CUSTOMER (templateType "2") + Run Once → 24h / 7d / 30d
 *   - BROWSING_HISTORY (templateType "3")    → 24h / 7d / 30d / 365d
 *
 * Troubleshooting Report (templateType "1") doesn't show a Time Period
 * dropdown at all in the classic UI, so it doesn't need its own option set.
 *
 * `TIME_PERIOD_OPTIONS` is kept as a backward-compatible alias for the
 * Customer Run-Once set (the most common case) so existing imports keep
 * working. `TIME_PERIOD_OPTIONS_TROUBLESHOOTING` is kept (under the same
 * legacy name) for the Review step's label-lookup union.
 */
export const TIME_PERIOD_OPTIONS_CUSTOMER = [
  { value: '1', label: 'Last 24 Hours' },
  { value: '2', label: 'Last 7 Days' },
  { value: '3', label: 'Last 30 Days' }
];

export const TIME_PERIOD_OPTIONS_TROUBLESHOOTING = [
  { value: '1', label: 'Last 24 Hours' },
  { value: '2', label: 'Last 7 Days' },
  { value: '3', label: 'Last 30 Days' },
  { value: '4', label: 'Last 365 Days' }
];

export const TIME_PERIOD_OPTIONS = TIME_PERIOD_OPTIONS_CUSTOMER;

/**
 * Default Frequency dropdown options (Step 1 of the wizard, only shown when
 * the user picks Scheduled for a Customer report). Values match the classic
 * UI's `frequency` codes ("2" = Weekly, "3" = Monthly).
 */
export const FREQUENCY_OPTIONS = [
  { value: '2', label: 'Weekly' },
  { value: '3', label: 'Monthly' }
];

export const REPEAT_TYPES = {
  RUN_ONCE: '1',
  SCHEDULED: '2'
};

/**
 * Captive-portal report period options (Step 1, CP type only).
 * Rolling presets carry a `days` lookback and ARE schedulable; "custom" reveals
 * a start/end date picker (capped at CP_CUSTOM_MAX_DAYS) and is one-shot only.
 */
export const CP_PERIOD_OPTIONS = [
  { value: '7', label: 'Last 7 Days', days: 7 },
  { value: '15', label: 'Last 15 Days', days: 15 },
  { value: '30', label: 'Last 30 Days', days: 30 },
  { value: 'custom', label: 'Custom Range' }
];
export const CP_PERIOD_CUSTOM = 'custom';
export const CP_CUSTOM_MAX_DAYS = 30;

/**
 * Report template type codes — kept byte-identical to the classic UI's
 * `ReportTemplateType` state values (see `reportConfig.js` lines 1267 / 1279
 * / 1293):
 *   "1" = Troubleshooting Report
 *   "2" = Customer Report
 *   "3" = Browsing History Report (only shown when the user has the right
 *         role + ICP enabled — currently unused in the MUI wizard)
 *
 * Widget groups returned by GET_WIDGET_LIST carry a matching `reportType`
 * field; the Pick Widgets step filters groups by it.
 */
export const REPORT_TEMPLATE_TYPES = {
  TROUBLESHOOTING: '1',
  CUSTOMER: '2',
  BROWSING_HISTORY: '3',
  // Captive Portal report (value "4") — currently presented as a disabled
  // option in the wizard's Report Type radios. When chosen the wizard
  // skips the Widget step (no widgets are picked / rearranged for this
  // report type), so the stepper shrinks to: Report Profile → Filter &
  // Scope → Review.
  CAPTIVE_PORTAL: '4'
};

/** Branding logo radio values - kept in sync with the classic UI. */
export const BRANDING_LOGOS = {
  VAR: '1',
  ORGANIZATION: '2'
};

/**
 * Format a Unix epoch (seconds) to a "DD MMM, YYYY HH:mm a.m./p.m." style date used in the table.
 * Returns "N/A" for falsy or non-numeric input.
 */
export const formatReportDate = (epochSeconds) => {
  if (!epochSeconds) return 'N/A';
  const n = Number(epochSeconds);
  if (!Number.isFinite(n) || n <= 0) return 'N/A';
  const d = new Date(n * 1000);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dd = String(d.getDate()).padStart(2, '0');
  const mmm = months[d.getMonth()];
  const yyyy = d.getFullYear();
  let hh = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, '0');
  const period = hh >= 12 ? 'p.m.' : 'a.m.';
  hh = hh % 12 || 12;
  return `${dd} ${mmm}, ${yyyy} ${String(hh).padStart(2, '0')}:${mm} ${period}`;
};
